import React, { useState, useMemo } from 'react';
import { Button } from '../UI';
import { kuesionerData } from '../../helpers/kuesioner';
import { calculateAgeMonths } from '../../helpers/utils';
import { getKpspScheduleKey } from '../../utils/date';
import { getAssessmentRecommendations } from '../../helpers/assessmentRecommendations';

export default function KpspForm({ child, onSave }) {
    const ageInMonths = useMemo(() => calculateAgeMonths(child.dob.toDate()), [child.dob]);
    const scheduleKey = useMemo(() => getKpspScheduleKey(ageInMonths), [ageInMonths]);
    const relevantQuestions = useMemo(() => scheduleKey ? kuesionerData.kpsp[scheduleKey] || [] : [], [scheduleKey]);

    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);

    const handleAnswer = (index, value) => {
        setAnswers(prev => ({ ...prev, [index]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (Object.keys(answers).length < relevantQuestions.length) {
            alert("Harap jawab semua pertanyaan.");
            return;
        }
        setLoading(true);
        const score = Object.values(answers).filter(v => v === 'Ya').length;
        const total = relevantQuestions.length;
        
        const recommendations = getAssessmentRecommendations('kpsp', score, total, ageInMonths);
        
        const data = { 
            perkembangan: { 
                status: recommendations.status, 
                interpretasi: recommendations.interpretation,
                rekomendasi: recommendations.recommendations,
                tindakLanjut: recommendations.followUp,
                tingkatUrgency: recommendations.urgency,
                detail: `Skor KPSP: ${score}/${total}`,
                answers: answers,
                questions: relevantQuestions 
            } 
        };
        onSave(data, recommendations.status);
    };

    if (relevantQuestions.length === 0) {
        return <p className="text-center text-brand-muted">Tidak ada kuesioner KPSP yang relevan untuk usia anak saat ini (Usia: {ageInMonths} bulan).</p>;
    }

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-100 to-cyan-100 p-6 rounded-2xl border border-blue-200">
                <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">🧠</span>
                    <h3 className="font-bold text-blue-800">Kuesioner Pra Skrining Perkembangan (KPSP)</h3>
                </div>
                <p className="text-blue-700 text-sm opacity-80">Untuk usia {scheduleKey} bulan - Jawab 'Ya' jika anak bisa melakukan, 'Tidak' jika belum bisa.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {relevantQuestions.map((item, index) => (
                    <div key={item.id || index} className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                        <div className="mb-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full text-sm font-bold mr-3">
                                {index + 1}
                            </span>
                            <label className="text-gray-800 font-medium text-base leading-relaxed">{item.q}</label>
                            {item.domain && (
                                <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                    {item.domain}
                                </span>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <label className={`
                                flex items-center justify-center px-6 py-3 rounded-2xl border-2 cursor-pointer
                                transition-all duration-300 transform hover:scale-105 active:scale-95
                                font-semibold text-sm shadow-md hover:shadow-lg
                                ${answers[index] === 'Ya' 
                                    ? 'border-green-400 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 shadow-lg' 
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-green-200 hover:bg-green-50'
                                }
                            `}>
                                <input
                                    type="radio"
                                    name={`answer-${index}`}
                                    value="Ya"
                                    checked={answers[index] === 'Ya'}
                                    onChange={() => handleAnswer(index, 'Ya')}
                                    className="sr-only"
                                    required
                                />
                                <span className="flex items-center space-x-2">
                                    <span className="text-lg">✅</span>
                                    <span>Ya</span>
                                </span>
                            </label>
                            <label className={`
                                flex items-center justify-center px-6 py-3 rounded-2xl border-2 cursor-pointer
                                transition-all duration-300 transform hover:scale-105 active:scale-95
                                font-semibold text-sm shadow-md hover:shadow-lg
                                ${answers[index] === 'Tidak' 
                                    ? 'border-red-400 bg-gradient-to-r from-red-100 to-pink-100 text-red-700 shadow-lg' 
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:bg-red-50'
                                }
                            `}>
                                <input
                                    type="radio"
                                    name={`answer-${index}`}
                                    value="Tidak"
                                    checked={answers[index] === 'Tidak'}
                                    onChange={() => handleAnswer(index, 'Tidak')}
                                    className="sr-only"
                                    required
                                />
                                <span className="flex items-center space-x-2">
                                    <span className="text-lg">❌</span>
                                    <span>Tidak</span>
                                </span>
                            </label>
                        </div>
                    </div>
                ))}
                <Button type="submit" variant="primary" className="w-full mt-8" disabled={loading}>
                    {loading ? '🔄 Menyimpan...' : '💾 Simpan Hasil KPSP'}
                </Button>
            </form>
        </div>
    );
}
